/**
 * Bepaalt de pending/not-classifiable status voor documenten ZONDER classificatie.
 * Raakt NIET de bestaande classificatie-badge-logica (confidence thresholds, CSS classes).
 * Wordt alleen aangeroepen als er geen classificatie is.
 */
export type PendingClassificationStatus = "pending" | "not-classifiable" | null;

export const CLASSIFIABLE_MIMES = new Set([
	"application/pdf",
	"image/jpeg",
	"image/jpg",
	"image/png",
	"image/tiff",
	"image/bmp",
	"image/heif",
]);

const NON_CLASSIFIABLE_MIMES = new Set([
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.ms-excel",
	"text/plain",
	"application/zip",
	"message/rfc822",
]);

const PENDING_WINDOW_MS = 5 * 60 * 1000;

/**
 * Input-shape die compatibel is met de Zero `dossierDocument` (`packages/zero/src/schema.ts`),
 * waar `uploadedAt` en `creationDate` als `number` (epoch ms) zijn opgeslagen.
 * Strings/Date worden voor robuustheid ook geaccepteerd; null/undefined zijn no-ops.
 */
export type ClassificationStatusInput = {
	contentType?: string | null;
	mimeType?: string | null;
	filename?: string | null;
	uploadedAt?: number | string | Date | null;
	creationDate?: number | string | Date | null;
};

/**
 * Retourneert pending-status voor een document ZONDER classificatie.
 * - "pending": classificeerbaar mimetype, <5 min oud
 * - "not-classifiable": bekend niet-classificeerbaar mimetype
 * - null: geen badge tonen (expired of onbekend mimetype)
 */
export function getPendingClassificationStatus(
	document: ClassificationStatusInput,
	options: { featureFlagEnabled?: boolean; now?: number } = {},
): PendingClassificationStatus {
	if (options.featureFlagEnabled !== true) return null;

	const mime =
		document.contentType ||
		document.mimeType ||
		mimeFromFilename(document.filename ?? undefined);
	if (mime && NON_CLASSIFIABLE_MIMES.has(mime)) return "not-classifiable";
	if (mime && CLASSIFIABLE_MIMES.has(mime)) {
		const uploaded = document.uploadedAt ?? document.creationDate;
		if (uploaded != null) {
			const ageMs = (options.now ?? Date.now()) - toEpochMs(uploaded);
			return ageMs >= 0 && ageMs < PENDING_WINDOW_MS ? "pending" : null;
		}
		return "pending"; // geen tijd → conservatief
	}
	return null; // onbekend mimetype = geen badge
}

function toEpochMs(value: number | string | Date): number {
	if (typeof value === "number") return value;
	if (value instanceof Date) return value.getTime();
	return new Date(value).getTime();
}

function mimeFromFilename(name?: string): string | undefined {
	if (!name) return undefined;
	const ext = name.toLowerCase().split(".").pop();
	const map: Record<string, string> = {
		pdf: "application/pdf",
		jpg: "image/jpeg",
		jpeg: "image/jpeg",
		png: "image/png",
		tiff: "image/tiff",
		tif: "image/tiff",
		docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		doc: "application/msword",
		xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		txt: "text/plain",
		zip: "application/zip",
		eml: "message/rfc822",
	};
	return ext ? map[ext] : undefined;
}
